import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import CalendarHeader from './CalendarHeader';
import ViewToggle from './ViewToggle';
import MonthView from './MonthView';
import WeekView from './WeekView';
import DayView from './DayView';
import EventListView from '../ListView/EventListView';
import QuickAddPopover from '../EventPopover/QuickAddPopover';
import EventSlideout from '../EventSlideout/EventSlideout';
import useEvents from '../hooks/useEvents';

/**
 * CalendarView - Main wrapper for calendar with view state management
 *
 * Features:
 * - View mode state (month/week/day)
 * - Current date navigation
 * - Category filters
 * - Location filter (role-based visibility)
 * - Quick-add popover coordination
 * - Event slideout coordination
 */
const CalendarView = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // View state
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'week' | 'day'
  const [displayMode, setDisplayMode] = useState('calendar'); // 'calendar' | 'list'
  const [currentDate, setCurrentDate] = useState(new Date());

  // Filter state
  const [categoryFilters, setCategoryFilters] = useState({
    external: true,
    internal: true,
    personal: true,
    schedule: true
  });
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  // Quick-add popover state
  const [quickAddState, setQuickAddState] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
    selectedDate: null,
    selectedTime: null
  });

  // Event slideout state
  const [slideoutState, setSlideoutState] = useState({
    isOpen: false,
    event: null,
    defaultEventType: null
  });

  // Memoize filter params to prevent infinite re-renders
  // Only recalculate when actual dependencies change
  const filterParams = useMemo(() => {
    let startDate, endDate;

    if (viewMode === 'month') {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      startDate = startOfWeek(monthStart, { weekStartsOn: 0 }).toISOString();
      endDate = endOfWeek(monthEnd, { weekStartsOn: 0 }).toISOString();
    } else if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
      startDate = weekStart.toISOString();
      endDate = weekEnd.toISOString();
    } else {
      const dayStart = new Date(currentDate);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(currentDate);
      dayEnd.setHours(23, 59, 59, 999);
      startDate = dayStart.toISOString();
      endDate = dayEnd.toISOString();
    }

    return {
      startDate,
      endDate,
      locationId: selectedLocationId || undefined
    };
  }, [currentDate, viewMode, selectedLocationId]);

  // Fetch events using custom hook
  const {
    events,
    locations,
    loading,
    error,
    refetch,
    createEvent,
    updateEvent,
    deleteEvent,
    changeEventStatus
  } = useEvents(filterParams);

  // Filter events by category (client-side for responsive UX)
  const filteredEvents = events.filter(event => {
    return categoryFilters[event.category] !== false;
  });

  // Handle URL parameters for opening modals
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const openModal = searchParams.get('openModal');
    const eventType = searchParams.get('eventType');

    if (openModal === 'event') {
      setSlideoutState({
        isOpen: true,
        event: null, // New event
        defaultEventType: eventType || 'appointment'
      });

      // Clear the URL params after handling
      searchParams.delete('openModal');
      searchParams.delete('eventType');
      navigate(location.pathname + (searchParams.toString() ? `?${searchParams.toString()}` : ''), {
        replace: true
      });
    }
  }, [location, navigate]);

  // Handle date click (open quick-add popover)
  // If quick-add is already open, just close it (user must click again to open for new date)
  const handleDateClick = (date, position, time = null) => {
    // If quick-add is currently open, just close it
    if (quickAddState.isOpen) {
      setQuickAddState({ isOpen: false, position: { x: 0, y: 0 }, selectedDate: null, selectedTime: null });
      return;
    }

    // Open quick-add for this date
    setQuickAddState({
      isOpen: true,
      position,
      selectedDate: date,
      selectedTime: time
    });
  };

  // Handle event click (open slideout for editing)
  const handleEventClick = (event) => {
    setQuickAddState({ isOpen: false, position: { x: 0, y: 0 }, selectedDate: null, selectedTime: null });
    setSlideoutState({
      isOpen: true,
      event,
      defaultEventType: null
    });
  };

  // Handle quick-add submission
  const handleQuickAddSubmit = async (eventData) => {
    try {
      await createEvent(eventData);
      setQuickAddState({ isOpen: false, position: { x: 0, y: 0 }, selectedDate: null, selectedTime: null });
    } catch (error) {
      console.error('Failed to create event:', error);
      throw error;
    }
  };

  // Handle "More Details" from quick-add (expand to slideout)
  const handleQuickAddExpand = (partialEventData) => {
    setQuickAddState({ isOpen: false, position: { x: 0, y: 0 }, selectedDate: null, selectedTime: null });
    setSlideoutState({
      isOpen: true,
      event: { ...partialEventData, id: null }, // Partial data, no ID = new event
      defaultEventType: partialEventData.eventType || 'appointment'
    });
  };

  // Close quick-add popover
  const handleQuickAddClose = () => {
    setQuickAddState({ isOpen: false, position: { x: 0, y: 0 }, selectedDate: null, selectedTime: null });
  };

  // Close slideout
  const handleSlideoutClose = () => {
    setSlideoutState({ isOpen: false, event: null, defaultEventType: null });
  };

  // Handle slideout save
  const handleSlideoutSave = async (eventData) => {
    try {
      if (eventData.id) {
        await updateEvent(eventData.id, eventData);
      } else {
        await createEvent(eventData);
      }
      setSlideoutState({ isOpen: false, event: null, defaultEventType: null });
    } catch (error) {
      console.error('Failed to save event:', error);
      throw error;
    }
  };

  // Handle slideout delete
  const handleSlideoutDelete = async (eventId) => {
    try {
      await deleteEvent(eventId);
      setSlideoutState({ isOpen: false, event: null, defaultEventType: null });
    } catch (error) {
      console.error('Failed to delete event:', error);
      throw error;
    }
  };

  // Persist task status and keep the open slideout's event copy in sync so
  // Mark Complete stays on Edit Event for the same id.
  const applyTaskStatus = async (eventId, newStatus) => {
    await changeEventStatus(eventId, newStatus);
    setSlideoutState((prev) => {
      if (!prev.isOpen || !prev.event || prev.event.id !== eventId) {
        return prev;
      }
      return {
        ...prev,
        event: { ...prev.event, status: newStatus }
      };
    });
  };

  // List-row checkbox: swallow errors (fire-and-forget UI).
  const handleToggleTaskStatus = async (eventId, newStatus) => {
    try {
      await applyTaskStatus(eventId, newStatus);
    } catch (error) {
      console.error('Failed to toggle task status:', error);
    }
  };

  // Handle "+X more" click - switch to Week view for that day
  const handleShowMore = (date) => {
    setCurrentDate(date);
    setViewMode('week');
  };

  // Handle clicking a day from adjacent month - navigate to that month
  const handleNavigateToMonth = (date) => {
    setCurrentDate(date);
  };

  // Render view based on viewMode
  const renderView = () => {
    const viewProps = {
      currentDate,
      events: filteredEvents,
      onDateClick: handleDateClick,
      onEventClick: handleEventClick,
      loading
    };

    switch (viewMode) {
      case 'week':
        return <WeekView {...viewProps} />;
      case 'day':
        return <DayView {...viewProps} />;
      case 'month':
      default:
        return <MonthView {...viewProps} onShowMore={handleShowMore} onNavigateToMonth={handleNavigateToMonth} />;
    }
  };

  return (
    <div className="relative">
      {/* Mobile/Tablet: View Toggle above header - centered, text variant */}
      <div className="lg:hidden flex justify-center mb-3">
        <ViewToggle
          displayMode={displayMode}
          setDisplayMode={setDisplayMode}
          variant="text"
        />
      </div>

      {/* Calendar Header with controls */}
      <CalendarHeader
        currentDate={currentDate}
        setCurrentDate={setCurrentDate}
        viewMode={viewMode}
        setViewMode={setViewMode}
        displayMode={displayMode}
        setDisplayMode={setDisplayMode}
        categoryFilters={categoryFilters}
        setCategoryFilters={setCategoryFilters}
        locations={locations}
        selectedLocationId={selectedLocationId}
        setSelectedLocationId={setSelectedLocationId}
      />

      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          <p className="text-sm">{error}</p>
          <button
            onClick={refetch}
            className="text-sm underline hover:no-underline mt-1"
          >
            Try again
          </button>
        </div>
      )}

      {/* Calendar or List View */}
      {displayMode === 'calendar' ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {renderView()}
        </div>
      ) : (
        <EventListView
          events={filteredEvents}
          loading={loading}
          onEventClick={handleEventClick}
          onToggleTaskStatus={handleToggleTaskStatus}
          categoryFilters={categoryFilters}
        />
      )}

      {/* Quick-Add Popover */}
      <QuickAddPopover
        isOpen={quickAddState.isOpen}
        position={quickAddState.position}
        selectedDate={quickAddState.selectedDate}
        selectedTime={quickAddState.selectedTime}
        onClose={handleQuickAddClose}
        onSubmit={handleQuickAddSubmit}
        onExpand={handleQuickAddExpand}
      />

      {/* Event Slideout */}
      <EventSlideout
        isOpen={slideoutState.isOpen}
        onClose={handleSlideoutClose}
        event={slideoutState.event}
        defaultEventType={slideoutState.defaultEventType}
        onSave={handleSlideoutSave}
        onDelete={handleSlideoutDelete}
        onStatusChange={applyTaskStatus}
        shiftMode={slideoutState.event?.eventType === 'shift'}
      />
    </div>
  );
};

export default CalendarView;
